import { useWebcam } from '../../../../hooks/useWebcam';
import { CaptureMode } from '../types';
import { useState } from 'react';

/**
 * One photo the KYC wizard collects, taken with the webcam or picked from disk.
 *
 * The wizard keeps a capture per step alive for its whole lifetime, so stepping
 * back to an earlier screen shows what was already taken there.
 */
export const usePhotoCapture = () => {
    const [file, setFile] = useState<File | null>(null);
    const [mode, setMode] = useState<CaptureMode>('Camera');
    const { imgSrc, webcamRef, setImgSrc, captureImage } = useWebcam();

    /** Switching between camera and upload discards whatever the other mode held. */
    const toggleMode = () => {
        setImgSrc(null);
        setFile(null);
        setMode(prev => prev === 'Camera' ? 'Upload' : 'Camera');
    };

    const captureOrRetake = () => {
        imgSrc ? setImgSrc(null) : captureImage();
    };

    const clear = () => {
        setFile(null);
        setImgSrc(null);
    };

    /** The captured image as an object URL, or `null` while nothing is captured. */
    const readPhoto = () => {
        if (mode === 'Upload') return file ? URL.createObjectURL(file) : null;
        return imgSrc;
    };

    return {
        mode,
        file,
        imgSrc,
        webcamRef,
        setFile,
        clear,
        readPhoto,
        toggleMode,
        captureOrRetake,
        hasPhoto: Boolean(imgSrc || file),
    };
};

export type PhotoCapture = ReturnType<typeof usePhotoCapture>;
